import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AdminUser, Client, TeamMember } from '../types';
import { AdminLayout } from '../components/Layout';

export function AdminDashboard() {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [campaigns, setCampaigns] = useState<{ name: string, client_id: number, created_at: string }[]>([]);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showCreateMember, setShowCreateMember] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'admin' | 'qa'>('admin');
  const [createdAccessCode, setCreatedAccessCode] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [editingCampaign, setEditingCampaign] = useState<string | null>(null);
  const [editCampaignName, setEditCampaignName] = useState('');
  const [editCampaignClientId, setEditCampaignClientId] = useState<number | null>(null);
  const [editingClientName, setEditingClientName] = useState<string | null>(null);
  const [editClientNameValue, setEditClientNameValue] = useState('');
  const [editingMember, setEditingMember] = useState<string | null>(null);
  const [editMemberNameValue, setEditMemberNameValue] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleAddCredit = async () => {
    if (!selectedClient) return;
    const amountStr = prompt('Enter credit amount ($):');
    if (!amountStr) return;
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount <= 0) { alert('Invalid amount'); return; }
    const note = prompt('Note (optional):') || '';
    try {
      setActionLoading(true);
      const data = await request(`/admin/clients/${selectedClient}/credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, note }),
      });
      if (data.success) {
        setClients(clients.map(c => c.id === selectedClient ? { ...c, balance: data.balance } : c));
        alert(`Credit added! New balance: $${data.balance.toFixed(2)}`);
      } else {
        alert(data.error || 'Failed');
      }
    } catch (err: any) { alert(err.message || 'Failed to add credit'); }
    finally { setActionLoading(false); }
  };

  const handleSetBalance = async () => {
    if (!selectedClient) return;
    const currentClient = clients.find(c => c.id === selectedClient);
    const balanceStr = prompt(`Set balance directly (current: $${(currentClient?.balance || 0).toFixed(2)}):`);
    if (!balanceStr) return;
    const balance = parseFloat(balanceStr);
    if (isNaN(balance)) { alert('Invalid number'); return; }
    try {
      setActionLoading(true);
      const data = await request(`/admin/clients/${selectedClient}/balance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ balance }),
      });
      if (data.success) {
        setClients(clients.map(c => c.id === selectedClient ? { ...c, balance: data.balance } : c));
        alert(`Balance set to $${data.balance.toFixed(2)}`);
      } else {
        alert(data.error || 'Failed');
      }
    } catch (err: any) { alert(err.message || 'Failed to set balance'); }
    finally { setActionLoading(false); }
  };

  const getApiBase = () => {
    return '/api';
  };

  const request = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${getApiBase()}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  useEffect(() => {
    const stored = localStorage.getItem('rewagent_admin');
    if (stored) setAdmin(JSON.parse(stored));
    else navigate('/admin');
  }, [navigate]);

  useEffect(() => {
    if (admin && searchParams.get('tab') !== 'access-codes' && searchParams.get('tab') !== 'leads' && searchParams.get('tab') !== 'campaigns' && searchParams.get('tab') !== 'logs') {
      fetchClients();
    }
  }, [admin, searchParams.get('tab')]);

  const fetchClients = async () => {
    try { const data = await request('/admin/clients'); setClients(data); } catch { }
    finally { setLoading(false); }
  };

  const fetchTeamMembers = async (clientId: string) => {
    setLoading(true);
    try { const data = await request(`/admin/clients/${clientId}/members`); setTeamMembers(data || []); } catch { }
    finally { setLoading(false); }
  };

  const fetchCampaigns = async (clientId: string) => {
    try { const data = await request(`/admin/campaigns?client_id=${clientId}`); setCampaigns(data || []); } catch { }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim() || !selectedClient) return;
    setActionLoading(true);
    try {
      await request('/admin/campaigns', { method: 'POST', body: JSON.stringify({ name: newCampaignName, client_id: selectedClient }) });
      await fetchCampaigns(selectedClient);
      setShowCreateCampaign(false);
      setNewCampaignName('');
    } catch { }
    finally { setActionLoading(false); }
  };

  const handleEditCampaign = async (oldName: string) => {
    setActionLoading(true);
    try {
      await request(`/admin/campaigns/${encodeURIComponent(oldName)}`, {
        method: 'PUT',
        body: JSON.stringify({
          new_name: editCampaignName !== oldName ? editCampaignName : undefined,
          new_client_id: editCampaignClientId,
        }),
      });
      setEditingCampaign(null);
      // Re-fetch both campaigns (may have moved) and clients (balance may have changed)
      if (selectedClient) await fetchCampaigns(selectedClient);
      await fetchClients();
    } catch (err: any) {
      alert(err?.message || 'Failed to update campaign');
    } finally { setActionLoading(false); }
  };

  const handleDeleteCampaign = async (name: string) => {
    if (!confirm(`Delete campaign "${name}"? This only works if the campaign has no leads.`)) return;
    setActionLoading(true);
    try {
      await request(`/admin/campaigns/${encodeURIComponent(name)}`, { method: 'DELETE' });
      if (selectedClient) await fetchCampaigns(selectedClient);
    } catch (err: any) {
      alert(err?.message || 'Failed to delete campaign');
    } finally { setActionLoading(false); }
  };

  const handleRenameClient = async () => {
    if (!selectedClient || !editClientNameValue.trim()) return;
    setActionLoading(true);
    try {
      await request(`/admin/clients/${selectedClient}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editClientNameValue.trim() }),
      });
      setEditingClientName(null);
      await fetchClients();
    } catch (err: any) {
      alert(err?.message || 'Failed to rename client');
    } finally { setActionLoading(false); }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) return;
    setActionLoading(true);
    try {
      await request('/admin/clients', { method: 'POST', body: JSON.stringify({ name: newClientName }) });
      await fetchClients();
      setShowCreateClient(false);
      setNewClientName('');
    } catch { }
    finally { setActionLoading(false); }
  };

  const handleCreateMember = async () => {
    if (!newMemberName.trim() || !selectedClient) return;
    setActionLoading(true);
    try {
      const data = await request(`/admin/clients/${selectedClient}/members`, {
        method: 'POST',
        body: JSON.stringify({ name: newMemberName.trim(), role: newMemberRole })
      });
      // Add the new member immediately to the list
      setTeamMembers([{
        id: data.id,
        name: newMemberName.trim(),
        access_code: data.access_code,
        is_active: 1,
        role: newMemberRole,
        client_name: clients.find(c => c.id === selectedClient)?.name || '',
        created_at: new Date().toISOString()
      }, ...teamMembers]);
      setNewMemberName('');
      setShowCreateMember(false);
    } catch {
      alert('Failed to create team member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenameMember = async (memberId: string) => {
    if (!selectedClient || !editMemberNameValue.trim()) return;
    setActionLoading(true);
    try {
      await request(`/admin/clients/${selectedClient}/members/${memberId}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editMemberNameValue.trim() })
      });
      setTeamMembers(teamMembers.map(m => m.id === memberId ? { ...m, name: editMemberNameValue.trim() } : m));
      setEditingMember(null);
    } catch (err: any) {
      alert(err?.message || 'Failed to rename team member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeMember = async (memberId: string) => {
    if (!selectedClient) return;
    if (!confirm('Are you sure you want to revoke this user\'s access? This will immediately log them out and disable their code.')) return;

    try {
      await request(`/admin/clients/${selectedClient}/members/${memberId}/revoke`, {
        method: 'PUT'
      });
      setTeamMembers(teamMembers.map(m => m.id === memberId ? { ...m, is_active: 0 } : m));
    } catch {
      alert('Failed to revoke access');
    }
  };

  const handleRestoreMember = async (memberId: string) => {
    if (!selectedClient) return;
    if (!confirm('Restore this user\'s access? They will be able to log in again with their existing code.')) return;

    try {
      await request(`/admin/clients/${selectedClient}/members/${memberId}/restore`, {
        method: 'PUT'
      });
      setTeamMembers(teamMembers.map(m => m.id === memberId ? { ...m, is_active: 1 } : m));
    } catch {
      alert('Failed to restore access');
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleLogout = async () => {
    try { await request('/auth/logout', { method: 'POST' }); } catch { }
    localStorage.removeItem('rewagent_admin');
    localStorage.removeItem('rewagent_token');
    navigate('/admin');
  };

  if (!admin) return null;

  return (
    <AdminLayout
      activeTab="clients"
      onTabChange={(tab) => {
        const baseTab = tab.split('?')[0];
        navigate(`/admin/${['leads', 'access-codes', 'campaigns', 'logs'].includes(baseTab) ? tab : 'dashboard'}`);
      }}
      onLogout={handleLogout}
    >
      <div className="p-10 max-w-7xl mx-auto">
        <div className="mb-10 flex justify-between items-end">
          <div>
            <h1 className="text-4xl font-bold text-[#1a1a1a] mb-2 tracking-tight">Clients</h1>
            <p className="text-[#6b635a] text-sm uppercase tracking-widest font-semibold">Manage system organizations</p>
          </div>
          <button onClick={() => setShowCreateClient(true)} className="px-5 py-2.5 bg-[#1a1a1a] text-[#f3ede3] hover:bg-[#000000] rounded-lg text-sm font-medium transition-all">
            + New Client
          </button>
        </div>

        {showCreateClient && (
          <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-6 mb-8 shadow-sm">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-4">Create New Client</h3>
            <div className="flex gap-4">
              <input type="text" value={newClientName} onChange={(e) => setNewClientName(e.target.value)} placeholder="Client Organization Name" className="flex-1 bg-[#f3ede3] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-3 focus:outline-none focus:border-[#1a1a1a]" autoFocus />
              <button onClick={handleCreateClient} disabled={actionLoading || !newClientName.trim()} className="px-6 py-3 bg-[#1a1a1a] text-[#f3ede3] rounded-lg text-sm font-medium disabled:opacity-50">Save</button>
              <button onClick={() => setShowCreateClient(false)} className="px-6 py-3 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] rounded-lg text-sm font-medium">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1 border-r border-[#e3dbcf] pr-8">
            <div className="space-y-2">
              {clients.map(client => (
                <button
                  key={client.id}
                  onClick={() => { setSelectedClient(client.id); fetchTeamMembers(client.id); fetchCampaigns(client.id); setCreatedAccessCode(null); }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all font-medium text-sm ${selectedClient === client.id ? 'bg-[#1a1a1a] text-[#f3ede3]' : 'text-[#6b635a] hover:bg-[#fcfbfa]'}`}
                >
                  <div className="flex justify-between items-center">
                    <span>{client.name}</span>
                    <span className={`text-xs font-bold ${(client.balance || 0) < 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                      ${(client.balance || 0).toFixed(2)}
                    </span>
                  </div>
                </button>
              ))}
              {!loading && clients.length === 0 && <div className="text-sm text-[#8c8578] py-4">No clients system yet.</div>}
            </div>
          </div>

          <div className="md:col-span-3">
            {selectedClient ? (
              <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl p-8 shadow-sm">
                <div className="flex justify-between items-center mb-8 pb-6 border-b border-[#e3dbcf]">
                  <div className="flex items-center gap-3">
                    {editingClientName === selectedClient ? (
                      <>
                        <input
                          type="text"
                          value={editClientNameValue}
                          onChange={(e) => setEditClientNameValue(e.target.value)}
                          className="text-2xl font-bold text-[#1a1a1a] bg-[#f3ede3] border border-[#e3dbcf] rounded-lg px-3 py-1 focus:outline-none focus:border-[#1a1a1a]"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameClient(); if (e.key === 'Escape') setEditingClientName(null); }}
                        />
                        <button onClick={handleRenameClient} disabled={actionLoading} className="px-3 py-1.5 bg-[#1a1a1a] text-[#f3ede3] rounded-lg text-xs font-bold">Save</button>
                        <button onClick={() => setEditingClientName(null)} className="px-3 py-1.5 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] rounded-lg text-xs">Cancel</button>
                      </>
                    ) : (
                      <>
                        <h2 className="text-2xl font-bold text-[#1a1a1a]">{clients.find(c => c.id === selectedClient)?.name || 'Client'}</h2>
                        <button
                          onClick={() => { setEditingClientName(selectedClient); setEditClientNameValue(clients.find(c => c.id === selectedClient)?.name || ''); }}
                          className="px-3 py-1.5 bg-[#f3ede3] text-[#6b635a] border border-[#e3dbcf] hover:bg-[#e3dbcf] rounded-lg text-xs font-medium transition-all"
                        >
                          Rename
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-3 items-center">
                    <button onClick={handleSetBalance} disabled={actionLoading} className="px-4 py-2 bg-[#1a1a1a] text-white hover:bg-black rounded-lg text-sm font-medium transition-all">
                      Set Balance
                    </button>
                    <button onClick={handleAddCredit} disabled={actionLoading} className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-sm font-medium transition-all">
                      + Add Credit
                    </button>
                    <button onClick={() => setShowCreateMember(true)} className="px-4 py-2 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#1a1a1a] hover:text-[#f3ede3] rounded-lg text-sm font-medium transition-all">
                      + Add Member
                    </button>
                  </div>
                </div>

                {showCreateMember && (
                  <div className="mb-8 p-6 bg-[#f3ede3] rounded-xl border border-[#e3dbcf]">
                    <h3 className="text-sm font-bold text-[#1a1a1a] mb-3 uppercase tracking-widest">New Member Details</h3>
                    <div className="flex gap-4">
                      <input type="text" value={newMemberName} onChange={(e) => setNewMemberName(e.target.value)} placeholder="Full Name" className="flex-1 bg-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a]" autoFocus />
                      <select value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value as 'admin' | 'qa')} className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a] text-sm font-medium">
                        <option value="admin">Owner</option>
                        <option value="qa">QA</option>
                      </select>
                      <button onClick={handleCreateMember} disabled={actionLoading || !newMemberName.trim()} className="px-5 py-2.5 bg-[#1a1a1a] text-[#f3ede3] rounded-lg text-sm font-medium disabled:opacity-50">Create</button>
                      <button onClick={() => setShowCreateMember(false)} className="px-5 py-2.5 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] rounded-lg text-sm">Cancel</button>
                    </div>
                  </div>
                )}

                {createdAccessCode && (
                  <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-xl">
                    <h3 className="text-green-800 font-bold mb-2">Member Created Successfully!</h3>
                    <p className="text-green-700 text-sm mb-4">Please copy and share this access code securely. It will not be shown again.</p>
                    <div className="flex items-center gap-3">
                      <code className="px-4 py-2.5 bg-white border border-green-200 rounded-lg text-[#1a1a1a] font-mono text-lg font-bold tracking-widest flex-1 text-center">
                        {createdAccessCode}
                      </code>
                      <button onClick={() => handleCopyCode(createdAccessCode)} className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
                        {copiedCode === createdAccessCode ? 'Copied!' : 'Copy Code'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {loading && <div className="text-sm text-[#8c8578]">Loading members...</div>}
                  {!loading && teamMembers.length === 0 && <div className="text-sm text-[#8c8578]">No team members found for this client.</div>}
                  {teamMembers.map(member => (
                    <div key={member.id} className="flex justify-between items-center p-4 bg-[#f3ede3] border border-[#e3dbcf] rounded-xl">
                      <div>
                        {editingMember === member.id ? (
                          <div className="flex items-center gap-2 mb-1">
                            <input
                              type="text"
                              value={editMemberNameValue}
                              onChange={(e) => setEditMemberNameValue(e.target.value)}
                              className="font-semibold text-[#1a1a1a] bg-[#fcfbfa] border border-[#e3dbcf] rounded px-2 py-1 text-sm focus:outline-none focus:border-[#1a1a1a]"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameMember(member.id); if (e.key === 'Escape') setEditingMember(null); }}
                            />
                            <button onClick={() => handleRenameMember(member.id)} disabled={actionLoading} className="px-2 py-1 bg-[#1a1a1a] text-[#f3ede3] rounded text-[10px] font-bold">Save</button>
                            <button onClick={() => setEditingMember(null)} className="px-2 py-1 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] rounded text-[10px]">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#1a1a1a]">{member.name}</span>
                            <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${member.role === 'qa' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-[#fcfbfa] text-[#1a1a1a] border-[#e3dbcf]'}`}>{member.role === 'admin' || !member.role ? 'owner' : member.role}</span>
                            <button
                              onClick={() => { setEditingMember(member.id); setEditMemberNameValue(member.name); }}
                              className="text-[10px] text-[#8c8578] hover:text-[#1a1a1a] underline ml-1"
                            >
                              edit
                            </button>
                          </div>
                        )}
                        <div className="text-xs text-[#8c8578] mt-1">Added {new Date(member.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <code className={`text-sm font-mono px-3 py-1.5 rounded-md border ${member.is_active ? 'text-[#6b635a] bg-[#fcfbfa] border-[#e3dbcf]' : 'text-red-600 bg-red-50 border-red-200 line-through opacity-60'}`}>{member.access_code}</code>
                        {member.is_active === 1 ? (
                          <>
                            <button onClick={() => handleCopyCode(member.access_code)} className="p-2 text-[#8c8578] hover:text-[#1a1a1a] hover:bg-[#fcfbfa] border border-transparent hover:border-[#e3dbcf] rounded-md transition-colors" title="Copy code">
                              {copiedCode === member.access_code ? (
                                <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                              )}
                            </button>
                            <button onClick={() => handleRevokeMember(member.id)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-md transition-colors" title="Revoke Access">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                            </button>
                          </>
                        ) : (
                          <button onClick={() => handleRestoreMember(member.id)} className="px-3 py-1 text-xs font-bold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors uppercase tracking-widest" title="Restore Access">
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Campaigns Section */}
                <div className="mt-10 pt-8 border-t border-[#e3dbcf]">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-[#1a1a1a]">Campaigns</h2>
                    <button onClick={() => setShowCreateCampaign(true)} className="px-4 py-2 bg-[#f3ede3] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#1a1a1a] hover:text-[#f3ede3] rounded-lg text-sm font-medium transition-all">
                      + Add Campaign
                    </button>
                  </div>

                  {showCreateCampaign && (
                    <div className="mb-6 p-6 bg-[#f3ede3] rounded-xl border border-[#e3dbcf]">
                      <h3 className="text-sm font-bold text-[#1a1a1a] mb-3 uppercase tracking-widest">New Campaign</h3>
                      <div className="flex gap-4">
                        <input type="text" value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))} placeholder="e.g. facebookad1" className="flex-1 bg-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2.5 focus:outline-none focus:border-[#1a1a1a]" autoFocus />
                        <button onClick={handleCreateCampaign} disabled={actionLoading || !newCampaignName.trim()} className="px-5 py-2.5 bg-[#1a1a1a] text-[#f3ede3] rounded-lg text-sm font-medium disabled:opacity-50">Create</button>
                        <button onClick={() => { setShowCreateCampaign(false); setNewCampaignName(''); }} className="px-5 py-2.5 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] rounded-lg text-sm">Cancel</button>
                      </div>
                      <p className="text-xs text-[#8c8578] mt-2">Only lowercase alphanumeric characters allowed</p>
                    </div>
                  )}

                  <div className="space-y-3">
                    {campaigns.length === 0 && <div className="text-sm text-[#8c8578]">No campaigns yet for this client.</div>}
                    {campaigns.map(camp => (
                      <div key={camp.name} className="p-4 bg-[#f3ede3] border border-[#e3dbcf] rounded-xl">
                        {editingCampaign === camp.name ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Campaign Name</label>
                                <input
                                  type="text"
                                  value={editCampaignName}
                                  onChange={(e) => setEditCampaignName(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                                  className="w-full bg-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]"
                                />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-[#6b635a] mb-1 block">Assigned Client</label>
                                <select
                                  value={editCampaignClientId ?? ''}
                                  onChange={(e) => setEditCampaignClientId(parseInt(e.target.value))}
                                  className="w-full bg-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-[#1a1a1a] px-4 py-2 focus:outline-none focus:border-[#1a1a1a]"
                                >
                                  {clients.map(cl => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditCampaign(camp.name)}
                                disabled={actionLoading}
                                className="px-4 py-2 bg-[#1a1a1a] text-[#f3ede3] rounded-lg text-xs font-bold disabled:opacity-50"
                              >
                                {actionLoading ? 'Saving...' : 'Save'}
                              </button>
                              <button onClick={() => setEditingCampaign(null)} className="px-4 py-2 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] rounded-lg text-xs">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-between items-center">
                            <code className="text-sm font-semibold text-[#1a1a1a] bg-[#e3dbcf]/30 px-3 py-1.5 rounded">{camp.name}</code>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-[#8c8578]">{new Date(camp.created_at).toLocaleDateString()}</span>
                              <button
                                onClick={() => { setEditingCampaign(camp.name); setEditCampaignName(camp.name); setEditCampaignClientId(camp.client_id); }}
                                className="px-3 py-1.5 bg-[#fcfbfa] text-[#1a1a1a] border border-[#e3dbcf] hover:bg-[#e3dbcf] rounded-lg text-xs font-medium transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCampaign(camp.name)}
                                disabled={actionLoading}
                                className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center border-2 border-dashed border-[#e3dbcf] rounded-2xl text-[#8c8578]">
                <svg className="w-12 h-12 mb-3 text-[#b8b0a5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p>Select a client to manage their team</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
